import * as constants from '@/map/map-constants';
import { MapboxInstanceMgr } from '@/map/map-instance';
import { generatePopupHtml } from '@/map/popup-maker';
import { PulsingDot } from './pulsing-dot';

// Class that manages the pulsing dots on the map
class PulsingDotMgr {
  constructor() {
    this.pulsingDots = {};
  }

  get map() {
    // eslint-disable-line class-methods-use-this
    return MapboxInstanceMgr.getInstance().mapInstance;
  }

  startDot = (key) => {
    const sourceId = `${PulsingDot.BASE_SOURCE_ID}-${key}`;
    const innerLayerId = `${PulsingDot.BASE_INNER_LAYER_ID}-${key}`;
    const outerLayerId = `${PulsingDot.BASE_OUTER_LAYER_ID}-${key}`;
    if (
      this.map.getLayer(innerLayerId) ||
      this.map.getLayer(outerLayerId) ||
      this.map.getSource(sourceId)
    ) {
      return;
    }

    this.pulsingDots[key].instance.start();
  };

  stopDot = (key) => {
    try {
      const sourceId = `${PulsingDot.BASE_SOURCE_ID}-${key}`;
      const innerLayerId = `${PulsingDot.BASE_INNER_LAYER_ID}-${key}`;
      const outerLayerId = `${PulsingDot.BASE_OUTER_LAYER_ID}-${key}`;
      if (
        !this.map ||
        !this.map.getLayer(innerLayerId) ||
        !this.map.getLayer(outerLayerId) ||
        !this.map.getSource(sourceId)
      ) {
        return;
      }
      this.pulsingDots[key].instance.stop();
    } catch (err) {
      // when switching cities it may happen that the map has been destroyed
      console.error('Error in pulsingDot.stopDot', err); // eslint-disable-line no-console
    }
  };

  stopAllDots = () => {
    Object.keys(this.pulsingDots).forEach((key) => {
      this.stopDot(key);
      this.deleteDot(key);
    });
  };

  /**
   * Stops all dots originating from the same component
   * @param {String} origin component where a dot instance render was called
   */
  stopDotsFromSameOrigin = (origin) => {
    Object.keys(this.pulsingDots).forEach((key) => {
      if (this.getDotOrigin(key) === origin) {
        this.stopDot(key);
        this.deleteDot(key);
      }
    });
  };

  reset = () => {
    // remove all pulsing dots popup
    Object.values(this.pulsingDots).forEach((dot) => dot.popup?.remove());
    this.stopAllDots();
    this.pulsingDots = {};
  };

  setDot = (key, pulsingDot, dotOrigin) => {
    this.pulsingDots[key] = {
      instance: pulsingDot,
      origin: dotOrigin,
    };
  };

  setDotWithInfoPopup = (key, pulsingDot, popup) => {
    this.pulsingDots[key] = {
      instance: pulsingDot,
      popup,
    };
  };

  deleteDot = (key) => delete this.pulsingDots[key];

  getDot = (key) => this.pulsingDots[key];

  getDotOrigin = (key) => this.pulsingDots[key].origin;

  /**
   * Returns a boolean indicating if property has a representing dot
   * @param {String} key Deckard ID of the property
   */
  #contains = (key) => !!this.getDot(key);

  /**
   * Adds a pulsing dot onto the map render
   * @param {Object} propertyData property data
   * @param {String} origin component where the function was called
   */
  highlightPropertyOnMap = (propertyData, origin) => {
    const { deckardid } = propertyData;

    // Sanity checks
    if (propertyData && !this.#contains(deckardid)) {
      const pulsingDot = new PulsingDot(propertyData);

      // Disables and removes all dots generated from hovering over property cards
      // then create a pulsing dot for the hovered on property
      this.stopDotsFromSameOrigin(origin);
      this.setDot(deckardid, pulsingDot, origin);

      pulsingDot.start();
    }
  };

  /**
   * Removes the corresponding pulsing dot form the map
   * @param {String} deckardid Deckard ID of the property
   */
  unhighlightPropertyOnMap = (deckardid) => {
    if (!!deckardid && this.#contains(deckardid)) {
      this.stopDot(deckardid);
      this.deleteDot(deckardid);
    }
  };

  createPopup = async (property, onCloseCb, platformIcons) => {
    const popup = await generatePopupHtml(
      this.map,
      property,
      undefined,
      undefined,
      platformIcons
    );
    popup.on('close', onCloseCb);
    return popup;
  };

  /**
   * Redirects user to main map view, zoomed and centered onto the featured property
   * with a modal of the property data
   * @param {Object} featuredProperty property data
   * @param {String} featuredDeckardid Deckard ID of the featured property
   */
  locateOnMapWithInfoPopup =
    (featuredProperty, featuredDeckardid, platformIcons, history) =>
    async () => {
      if (!featuredDeckardid || this.#contains(featuredDeckardid)) {
        return;
      }
      this.map.setZoom(constants.HIGHLIGHT_PROPERTY_ZOOM);
      this.map.setCenter(featuredProperty.lnglat);
      const pulsingDot = new PulsingDot(featuredProperty);
      const popupOnCloseCb = () => {
        this.stopDot(pulsingDot.deckardid);
        this.deleteDot(pulsingDot.deckardid);

        // Clear the 'featured' query parameter from URL
        if (history && history.location && history.push) {
          const queryParams = new URLSearchParams(history.location.search);
          if (queryParams.has('featured')) {
            queryParams.delete('featured');
            // Preserve other location properties like pathname, hash, and state
            const { pathname, hash, state } = history.location;
            history.replace({
              pathname,
              search: queryParams.toString(),
              hash,
              state,
            });
          }
        }
      };
      const popup = await this.createPopup(
        featuredProperty,
        popupOnCloseCb,
        platformIcons
      );
      this.setDotWithInfoPopup(featuredDeckardid, pulsingDot, popup);
      pulsingDot.start();
    };
}

const pulsingDotMgr = new PulsingDotMgr();

export { pulsingDotMgr };
